import { useRef, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg, webUrl } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { Avatar } from "./kit";

const MAX = 5 * 1024 * 1024;

const upload = async (path, file) => {
  if (!file.type.startsWith("image/")) throw new Error("Pick an image");
  if (file.size > MAX) throw new Error("Photos must be 5 MB or smaller");
  const body = new FormData();
  body.append("file", file);
  return (await web.post(path, body)).data;
};

/** Avatar with a camera button; picks an image and posts it to `path`. */
export const PhotoPicker = ({ name, src, size = "h-24 w-24", path, onDone, testid, small }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const data = await upload(path, file);
      toast.success("Photo updated");
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative shrink-0">
      <Avatar key={src ?? "none"} name={name} src={src} size={size} />
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" data-testid={testid} onClick={() => input.current?.click()} disabled={busy} aria-label="Change photo"
        className={`absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-[#39FF14] text-[#0A0E1A] ring-2 ring-[#0A0E1A] transition-transform hover:scale-110 ${small ? "h-5 w-5" : "h-8 w-8"} ${busy ? "animate-pulse" : ""}`}>
        <Camera className={small ? "h-3 w-3" : "h-4 w-4"} />
      </button>
    </div>
  );
};

/** The signed-in user's own profile picture (players: also their roster photo). */
export const MyPhoto = ({ size = "h-10 w-10", testid = "my-photo-button" }) => {
  const { me, membership } = useAuth();
  const mine = useApi(me && "/me/photo", web);
  const isPlayer = membership?.role === "player" && membership?.player_id;
  const path = isPlayer ? `/teams/${membership.team_id}/players/${membership.player_id}/photo` : "/me/photo";
  return <PhotoPicker small name={me?.name ?? ""} src={webUrl(mine.data?.photo_url)} size={size} path={path} onDone={mine.reload} testid={testid} />;
};
